import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  SubmitHandler,
  useForm,
  useFormState,
  useWatch,
} from "react-hook-form";

import { FormPanel } from "../../../../components/FormPanel";
import { ConfirmDetail } from "../../../../components/PopUp/confirmShared";
import { notify } from "../../../../components/Notification";
import {
  getErrorMessages,
  ErrorResponse,
  useDeletePriceOverrideMutation,
  useGetPriceOverridesQuery,
  useRecordPriceOverrideMutation,
} from "../../../../services";
import { toIsoDate } from "../../../../utils/dates";
import { mapPriceOverrideError } from "./overrideErrors";
import { FORM_ID } from "./constants";
import { PriceOverrideFields } from "./PriceOverrideFields";
import {
  PriceOverrideFormValues,
  PriceOverridePanelProps,
  InstrumentOption,
} from "./types";

const EYEBROW = "Private valuation";

const toInstrumentOption = (
  instrument: PriceOverridePanelProps["instruments"][number],
): InstrumentOption => ({
  value: String(instrument.id),
  label: `${instrument.symbol} · ${instrument.name}`,
  instrument,
});

const defaultValues = (
  instrument: InstrumentOption | null,
): PriceOverrideFormValues => ({
  instrument,
  date: null,
  priceNative: "",
  fxToEur: "",
});

const buildDiscardDetails = (): ConfirmDetail => ({
  title: "Unsaved private price",
});

// Ledger-only manager for a user's private historical prices: entries repair only the caller's
// valuations, never the shared market catalogue.
export const PriceOverridePanel: React.FC<PriceOverridePanelProps> = ({
  isOpen,
  onClose,
  initialInstrument,
  instruments,
}) => {
  const [panelError, setPanelError] = useState<string | null>(null);
  const [showFxRateAfterError, setShowFxRateAfterError] = useState(false);
  const [recordPriceOverride] = useRecordPriceOverrideMutation();
  const [deletePriceOverride, { isLoading: isDeleting }] =
    useDeletePriceOverrideMutation();

  const instrumentOptions = useMemo(
    () =>
      [
        ...new Map(
          instruments.map((instrument) => [instrument.id, instrument]),
        ).values(),
      ]
        .sort((left, right) => left.symbol.localeCompare(right.symbol))
        .map(toInstrumentOption),
    [instruments],
  );
  const fixedOption = useMemo(
    () => (initialInstrument ? toInstrumentOption(initialInstrument) : null),
    [initialInstrument],
  );
  // A chart gap with several plausible positions must not guess a target.
  // Row entry is the only path allowed to preselect an instrument.
  const defaultInstrument = fixedOption;
  const isInstrumentFixed = fixedOption !== null;

  const form = useForm<PriceOverrideFormValues>({
    defaultValues: defaultValues(defaultInstrument),
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { control, reset, setError, setValue } = form;
  const { isSubmitting } = useFormState({ control });
  const selectedInstrument = useWatch({ control, name: "instrument" });

  // Blanking is an *opening* action: `defaultInstrument` is re-derived every render, so resetting on
  // it would discard what the user typed when a target resolves while the panel is open.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      wasOpen.current = false;
      return;
    }
    if (wasOpen.current) {
      return;
    }
    wasOpen.current = true;
    reset(defaultValues(defaultInstrument));
    setPanelError(null);
    setShowFxRateAfterError(false);
  }, [isOpen, defaultInstrument, reset]);

  // The late-arriving target only ever fills an empty field, so it never overwrites a choice made since.
  useEffect(() => {
    if (!isOpen || !defaultInstrument || selectedInstrument) {
      return;
    }
    setValue("instrument", defaultInstrument);
  }, [isOpen, defaultInstrument, selectedInstrument, setValue]);

  const selectedInstrumentId = selectedInstrument?.instrument.id;
  // `currentData`, not `data`: a retained row's Delete would pair the previous instrument's date
  // with the newly selected ticker.
  const { currentData: fetchedOverrides, isFetching: isFetchingOverrides } =
    useGetPriceOverridesQuery(selectedInstrumentId ?? 0, {
      skip: selectedInstrumentId === undefined,
    });
  const isLoadingOverrides =
    selectedInstrumentId !== undefined &&
    isFetchingOverrides &&
    fetchedOverrides === undefined;
  const overrides = useMemo(
    () =>
      [...(fetchedOverrides ?? [])].sort((left, right) =>
        right.date.localeCompare(left.date),
      ),
    [fetchedOverrides],
  );
  const isEuroInstrument =
    selectedInstrument?.instrument.nativeCurrency === "EUR";
  const showFxRate = !isEuroInstrument || showFxRateAfterError;
  const isWriteInFlight = isSubmitting || isDeleting;

  const handleClose = () => {
    setPanelError(null);
    setShowFxRateAfterError(false);
    onClose();
  };

  const onSubmit: SubmitHandler<PriceOverrideFormValues> = async (values) => {
    setPanelError(null);
    if (!values.instrument || !values.date || values.priceNative === "") {
      setPanelError("Choose an instrument, date, and price before saving.");
      return;
    }
    // A euro instrument sends no rate unless the server asked for one; otherwise only a typed rate
    // travels (`undefined` is left out of the request body).
    const isEuroSubmission =
      values.instrument.instrument.nativeCurrency === "EUR";
    const canSendFxRate = !isEuroSubmission || showFxRateAfterError;
    const fxToEur =
      canSendFxRate && values.fxToEur !== "" ? values.fxToEur : undefined;
    try {
      const result = await recordPriceOverride({
        instrumentId: values.instrument.instrument.id,
        date: toIsoDate(values.date),
        priceNative: values.priceNative,
        fxToEur,
      }).unwrap();
      notify.success(
        result.replacedExisting
          ? "Private price replaced"
          : "Private price added",
      );
      reset(defaultValues(values.instrument));
      setShowFxRateAfterError(false);
    } catch (error: unknown) {
      const mapped = mapPriceOverrideError(
        getErrorMessages(error as ErrorResponse),
      );
      for (const [field, message] of Object.entries(mapped.fieldErrors)) {
        setError(field as keyof PriceOverrideFormValues, {
          type: "server",
          message,
        });
      }
      setShowFxRateAfterError(mapped.shouldRevealFxRate);
      setPanelError(mapped.panelError);
    }
  };

  const handleDelete = async (date: string) => {
    if (!selectedInstrument) {
      return;
    }
    setPanelError(null);
    try {
      await deletePriceOverride({
        instrumentId: selectedInstrument.instrument.id,
        date,
      }).unwrap();
      notify.success("Private price removed");
    } catch (error: unknown) {
      const mapped = mapPriceOverrideError(
        getErrorMessages(error as ErrorResponse),
      );
      setPanelError(
        mapped.panelError ?? "Unable to remove this private price.",
      );
    }
  };

  const fieldsProps = {
    control,
    instrumentOptions,
    defaultInstrument,
    selectedInstrument,
    overrides,
    isLoadingOverrides,
    isWriteInFlight,
    showFxRate,
    isInstrumentFixed,
    onDelete: handleDelete,
  };

  return (
    <FormPanel
      open={isOpen}
      onClose={handleClose}
      error={panelError}
      ariaLabel="Manage private prices"
      eyebrow={EYEBROW}
      title="Set my price"
      form={form}
      resetValues={defaultValues(defaultInstrument)}
      formId={FORM_ID}
      onSubmit={onSubmit}
      submitLabel="Save price"
      isSubmitDisabled={isDeleting}
      discard={{
        title: "Discard private price?",
        confirmLabel: "Discard",
        details: buildDiscardDetails,
      }}
      mobile={{
        title: "Set my price",
        submitLabel: "Save",
        fields: <PriceOverrideFields {...fieldsProps} policyLabel={EYEBROW} />,
      }}
    >
      <PriceOverrideFields {...fieldsProps} />
    </FormPanel>
  );
};
